import * as THREE from "three";

export interface DevSceneState {
  health: number;
  maxHealth: number;
  tokens: number;
  tier: number;
  depth: number;
  boss: boolean;
  time: number;
}

export interface DevScene {
  group: THREE.Group;
  update(state: DevSceneState): void;
}

const MAX_TOKENS_SHOWN = 8;
const BUG_BASE_RADIUS = 1.4;
const BUG_MAX_RADIUS = 2.6;
const RED = new THREE.Color(0xe94560);
const GREEN = new THREE.Color(0x3ddc97);

export function createDevScene(): DevScene {
  const group = new THREE.Group();

  addFloor(group);
  const desk = addDesk(group);
  const towers = addTowers(group);
  const fragments = addCodeFragments(group);
  const bug = addBug(group);
  const tokens = addTokens(desk);

  return {
    group,
    update(state: DevSceneState): void {
      const healthFrac = clamp(
        state.health / Math.max(1, state.maxHealth),
        0,
        1
      );

      const towerCount = state.boss ? 0 : state.tier;
      towers.forEach((tower, i) => {
        tower.visible = i < towerCount;
      });

      const bugAngle = state.time * 0.8;
      const radius =
        BUG_BASE_RADIUS + (1 - healthFrac) * (BUG_MAX_RADIUS - BUG_BASE_RADIUS);
      bug.group.position.x = Math.cos(bugAngle) * radius;
      bug.group.position.z = Math.sin(bugAngle) * radius;
      bug.group.position.y = 0.9 + (1 - healthFrac) * 0.6;
      bug.group.rotation.x = state.time * 0.6;
      bug.group.rotation.y = state.time * 0.9;

      const bugColor = new THREE.Color().lerpColors(RED, GREEN, healthFrac);
      bug.material.color.copy(bugColor);
      const bugScale = (state.boss ? 1.6 : 1) * (1.35 - healthFrac * 0.35);
      bug.group.scale.setScalar(bugScale);

      fragments.forEach((frag, i) => {
        frag.position.y =
          frag.userData.baseY +
          Math.sin(state.time * (0.4 + i * 0.13) + frag.userData.phase) * 0.18;
        frag.rotation.y = state.time * (0.3 + i * 0.05);
      });

      tokens.forEach((token, i) => {
        token.visible = i < state.tokens;
        token.rotation.y = state.time * (0.8 + i * 0.06);
      });
    },
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function addFloor(group: THREE.Group): void {
  const plane = new THREE.Mesh(
    new THREE.PlaneGeometry(12, 12),
    new THREE.MeshStandardMaterial({ color: 0x101231, roughness: 0.9 })
  );
  plane.rotation.x = -Math.PI / 2;
  plane.position.y = -1.1;
  group.add(plane);

  const grid = new THREE.GridHelper(12, 24, 0x2b2f5e, 0x232648);
  grid.position.y = -1.08;
  grid.name = "devGrid";
  group.add(grid);
}

function addDesk(group: THREE.Group): THREE.Group {
  const desk = new THREE.Group();
  desk.position.set(0, -0.95, 0);

  const top = new THREE.Mesh(
    new THREE.BoxGeometry(2.4, 0.15, 1.1),
    new THREE.MeshStandardMaterial({ color: 0x3a4168, roughness: 0.55 })
  );
  top.position.y = 0.42;
  desk.add(top);

  const monitor = makeMonitor();
  monitor.position.set(0.0, 0.98, 0.15);
  desk.add(monitor);

  const sideMonitor = makeMonitor();
  sideMonitor.scale.setScalar(0.6);
  sideMonitor.position.set(0.95, 0.55, 0.45);
  desk.add(sideMonitor);

  const coffee = new THREE.Mesh(
    new THREE.CylinderGeometry(0.09, 0.07, 0.2, 12),
    new THREE.MeshStandardMaterial({ color: 0x4a1f22, roughness: 0.4 })
  );
  coffee.position.set(-0.85, 0.42, 0.1);
  coffee.rotation.z = 0.08;
  desk.add(coffee);

  group.add(desk);
  return desk;
}

function makeMonitor(): THREE.Object3D {
  const monitor = new THREE.Group();

  const screen = new THREE.Mesh(
    new THREE.BoxGeometry(0.8, 0.46, 0.06),
    new THREE.MeshStandardMaterial({
      color: 0xd8e0ff,
      emissive: 0x4f8cff,
      emissiveIntensity: 0.5,
    })
  );
  screen.position.y = 0.2;
  monitor.add(screen);

  const frame = new THREE.Mesh(
    new THREE.BoxGeometry(0.92, 0.56, 0.06),
    new THREE.MeshStandardMaterial({ color: 0x22263f, roughness: 0.6 })
  );
  frame.position.y = 0.2;
  monitor.add(frame);

  const stand = new THREE.Mesh(
    new THREE.BoxGeometry(0.12, 0.25, 0.06),
    new THREE.MeshStandardMaterial({ color: 0x22263f, roughness: 0.6 })
  );
  stand.position.y = -0.2;
  monitor.add(stand);

  return monitor;
}

function addTowers(group: THREE.Group): THREE.Mesh[] {
  const towers: THREE.Mesh[] = [];
  const positions = [
    new THREE.Vector3(-1.55, 0, -0.9),
    new THREE.Vector3(1.65, 0, -1.0),
    new THREE.Vector3(0.6, 0.8, -1.7),
  ];

  positions.forEach((pos, i) => {
    const width = 0.42 - i * 0.05;
    const tower = new THREE.Mesh(
      new THREE.BoxGeometry(width, 1.2 + i * 0.35, width),
      makeServerMaterial()
    );
    tower.position.copy(pos);
    tower.position.y += (1.2 + i * 0.35) / 2;
    tower.name = `serverTower${i}`;
    towers.push(tower);
    group.add(tower);
  });

  return towers;
}

function makeServerMaterial(): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: 0x17224a,
    emissive: 0x1a2a66,
    emissiveIntensity: 0.35,
    metalness: 0.6,
    roughness: 0.4,
  });
}

function addCodeFragments(group: THREE.Group): THREE.Mesh[] {
  const fragments: THREE.Mesh[] = [];
  const geometry = new THREE.OctahedronGeometry(0.14, 0);
  const material = new THREE.MeshStandardMaterial({
    color: 0x63d2ff,
    emissive: 0x2a7fff,
    emissiveIntensity: 0.7,
    wireframe: true,
  });

  const specs = [
    { x: -1.2, z: 1.4, phase: 0 },
    { x: 1.3, z: 1.2, phase: 1.7 },
    { x: 0.2, z: 1.9, phase: 3.1 },
    { x: -0.4, z: -1.9, phase: 4.2 },
    { x: 1.1, z: -1.6, phase: 5.0 },
  ];

  specs.forEach((spec, i) => {
    const frag = new THREE.Mesh(geometry, material);
    frag.userData = { baseY: 1.1 + (i % 3) * 0.35, phase: spec.phase };
    frag.position.set(spec.x, frag.userData.baseY, spec.z);
    fragments.push(frag);
    group.add(frag);
  });

  return fragments;
}

function addBug(group: THREE.Group): {
  group: THREE.Group;
  material: THREE.MeshStandardMaterial;
} {
  const bugGroup = new THREE.Group();
  bugGroup.name = "bug";

  const solid = new THREE.BoxGeometry(0.5, 0.5, 0.5);
  const material = new THREE.MeshStandardMaterial({
    color: 0x3ddc97,
    emissive: 0x14602f,
    emissiveIntensity: 0.5,
    roughness: 0.3,
  });
  const core = new THREE.Mesh(solid, material);
  bugGroup.add(core);

  const spikes = new THREE.Mesh(
    new THREE.IcosahedronGeometry(0.38, 0),
    new THREE.MeshStandardMaterial({
      color: 0x103a24,
      wireframe: true,
      emissive: 0x14602f,
      emissiveIntensity: 0.3,
    })
  );
  bugGroup.add(spikes);

  group.add(bugGroup);
  return { group: bugGroup, material };
}

function addTokens(desk: THREE.Group): THREE.Object3D[] {
  const tokenMeshes: THREE.Object3D[] = [];
  const geometry = new THREE.TorusGeometry(0.14, 0.05, 8, 18);
  const material = new THREE.MeshStandardMaterial({
    color: 0xf2a33c,
    emissive: 0xa8661f,
    emissiveIntensity: 0.55,
    metalness: 0.4,
    roughness: 0.3,
  });

  for (let i = 0; i < MAX_TOKENS_SHOWN; i += 1) {
    const ring = new THREE.Group();
    ring.name = `scoreRing${i}`;
    ring.rotation.x = i % 2 === 0 ? 0 : Math.PI / 2;

    const angle = (i / MAX_TOKENS_SHOWN) * Math.PI * 2;
    const token = new THREE.Mesh(geometry, material);
    token.position.set(Math.cos(angle) * 1.05, 0, Math.sin(angle) * 0.4);
    ring.add(token);

    desk.add(ring);
    tokenMeshes.push(ring);
  }

  return tokenMeshes;
}
